import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import debounce from 'lodash/debounce'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { updateConstructionYear } from '../../store/lead/leadSlice'
import './ConstructionYearStep.scss'
import { useReportViewEvent } from '../../hooks'
import { enrichLead } from '../../store/lead/thunks'
import { EnrichLeadValuationType } from '../../@types/enrichLeadValuationtype'
import { reportTrackingEvent } from '../../tracking/thunks'
import { TextInput } from '../../components/styled/TextInput/TextInput'
import { ConstructionYearStepIcon } from '../../components/icons/StepIcons/ConstructionYearStepIcon'
import { RealEstateType } from '../../@types/realEstateType'
import { ReduxState } from '../../store/reducers'
import { richValuationFlowProgress } from '../../progress/valuationProgressHelper'
import { STEP } from '../../@types/state'

export interface ConstructionStepProps {
  goToNextStep: () => unknown
  goToErrorStep: () => unknown
  goToPreviousStep?: () => unknown
}

const getHeadline = (realEstateType?: RealEstateType) => {
  switch (realEstateType) {
    case RealEstateType.HOUSE:
      return 'Wann wurde das Haus gebaut?'
    case RealEstateType.APARTMENT:
      return 'Wann wurde die Wohnung gebaut?'
    default:
      return 'Wann wurde die Immobilie gebaut?'
  }
}

const possibleStartValues = '12'

export const ConstructionYearStep: FC<ConstructionStepProps> = (props) => {
  useReportViewEvent({ pageTitle: 'constructionyear' })
  const {
    config: { theme },
    lead: { constructionYear, id: leadId, realEstateType },
  } = useSelector((state: ReduxState) => state)
  const [lastQuery, setLastQuery] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>(constructionYear ? constructionYear.toString() : '')
  const currentYear = new Date().getFullYear()
  const validateConstructionYear =
    !!constructionYear && constructionYear >= 1800 && constructionYear <= currentYear && inputValue.length === 4
  const valuationBarProgress = richValuationFlowProgress(STEP.CONSTRUCTION_YEAR)

  const dispatch = useDispatch()

  const handleQueryChanged = (query: string) => {
    setLastQuery(query)
  }
  const isValidConstructionYear = !!inputValue && inputValue === lastQuery

  const constructionYearChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target
    if (value === '' || possibleStartValues.includes(value[0])) {
      setInputValue(value)
    }
  }

  const goToNextStep = () => {
    if (validateConstructionYear) {
      if (!leadId) {
        console.error('Lead id prop does not exist')
        props.goToErrorStep()
        return
      }

      dispatch(
        enrichLead({
          enrichValuationType: EnrichLeadValuationType.SINGLE_VALUATION,
          valuationForStep: STEP.REAL_ESTATE_SUBTYPE,
          pageTitle: 'constructionyear',
          request: {
            id: leadId,
            constructionyear: `${constructionYear}`,
          },
        })
      )

      dispatch(
        reportTrackingEvent({
          pageTitle: 'constructionyear',
          label: 'constructionyear_submit',
          inputValue: `${constructionYear}`,
          eventName: 'lead_input',
          customDimensions: {
            ga_cd_lead_engine_input_value: `${constructionYear}`,
          },
        })
      )
      props.goToNextStep()
    }
  }

  const constructionYearValidation = useCallback(debounce(handleQueryChanged, 2000), [])

  useEffect(() => {
    if (inputValue) {
      if (updateConstructionYear) {
        dispatch(updateConstructionYear(parseInt(inputValue)))
      }

      constructionYearValidation(inputValue)
    }
  }, [inputValue])

  return (
    <StepTemplate
      icon={<ConstructionYearStepIcon />}
      headline={getHeadline(realEstateType)}
      isNextEnabled={validateConstructionYear}
      goToNextStep={goToNextStep}
      dataTestingButton='construction-next-button'
      goToPreviousStep={props.goToPreviousStep}
      valuationBarProgress={valuationBarProgress}
      backgroundColor={theme.background}
      textColor={theme.text}
    >
      <div className='construction-container' data-testid='construction-container'>
        <span className='input-label align-left'>Baujahr</span>
        <div className='input-text-container'>
          <span className='construction-input-affix font-lightgray'></span>
          <TextInput
            id='construction-year'
            type='tel'
            label={''}
            data-testing='construction-input'
            data-testid='construction-input'
            placeholder='z.B. 1990'
            maxLength={4}
            value={inputValue || ''}
            onChange={constructionYearChange}
            success={validateConstructionYear}
            valid={!isValidConstructionYear}
            autoFocus
            backgroundColor={theme.background}
            color={theme.text}
            textColor={theme.text}
            borderColor={theme.secondary}
            className={'input-text'}
          />
        </div>
      </div>
    </StepTemplate>
  )
}

export default ConstructionYearStep
